import styled from "@emotion/styled";
import { Skeleton } from "@mui/material";
import Fuse from "fuse.js";
import i18next from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { useTranslation } from "react-i18next";
import { FiSearch } from "react-icons/fi";
import YouTube from "react-youtube";
import { useCentralStore } from "stores";

export const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const setHeaderTitle = useCentralStore(state => state.setHeaderTitle);

  const { t } = useTranslation();
  const auth = useAuthUser();
  const firstName = auth()?.first_name;

  const helpVideos = useMemo(
    () => [
      {
        id: 1,
        title: t("Help.Videos.CreateAccount"),
        videoId: "rURbBctLTHo"
      },
      {
        id: 2,
        title: t("Help.Videos.ResetPassword"),
        videoId: "NH1iXSrviOU"
      },
      {
        id: 3,
        title: t("Help.Videos.CreateChurch"),
        videoId: "_WDRKxbz_3c"
      },
      {
        id: 4,
        title: t("Help.Videos.RegisterMember"),
        videoId: "JiI7X1I52XY"
      },
      {
        id: 5,
        title: t("Help.Videos.RegisterMemberByLink"),
        videoId: "wNYPH1W_yjM"
      },
      {
        id: 6,
        title: t("Help.Videos.ImportMembers"),
        videoId: "R4AtQNy7wjk"
      },
      {
        id: 7,
        title: t("Help.Videos.UseDashboard"),
        videoId: "QV6AgyQk4BE"
      },
      {
        id: 8,
        title: t("Help.Videos.SendMessage"),
        videoId: "FOPyPHjrsc4"
      },
      {
        id: 9,
        title: t("Help.Videos.CreateTeam"),
        videoId: "wTVvqU_e7SQ"
      },
      {
        id: 10,
        title: t("Help.Videos.RegisterEmployee"),
        videoId: "V-unNWNAWoQ"
      }
    ],
    [t]
  );

  const [filteredHelpVideos, setFilteredHelpVideos] = useState(
    () => helpVideos
  );

  useEffect(() => {
    setHeaderTitle(t("Help.Help.HelpCenter"));
    return () => setHeaderTitle("");
  }, [i18next.language]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredHelpVideos(helpVideos);
      return;
    }

    const fuse = new Fuse(helpVideos, {
      keys: ["title"],
      threshold: 0.3
    });

    const results = fuse.search(searchTerm);
    setFilteredHelpVideos(results.map(result => result.item));
  }, [searchTerm, helpVideos]);

  const handleSearch = e => {
    e.preventDefault();
    setSearchTerm(searchQuery);
  };

  return (
    <Container>
      <SearchSection>
        <h1>{t("Help.Help.Welcome").replace("%name", firstName || "")}</h1>
        <p>{t("Help.Help.SearchDescription")}</p>
        <SearchBar as="form" onSubmit={handleSearch}>
          <input
            type="text"
            placeholder={t("Help.Help.SearchTopics")}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <SearchButton type="submit">
            {t("Help.Help.Search")} <FiSearch />
          </SearchButton>
        </SearchBar>
      </SearchSection>

      <VideosGrid>
        {filteredHelpVideos.length > 0 ? (
          filteredHelpVideos.map(video => (
            <HelpVideoCard key={video.id} video={video} />
          ))
        ) : (
          <NoResults>
            <h3>{t("Help.Help.NoResults")}</h3>
            <p>{t("Help.Help.TryAgain")}</p>
          </NoResults>
        )}
      </VideosGrid>
    </Container>
  );
};

const HelpVideoCard = ({ video }) => {
  const [isLoading, setIsLoading] = useState(true);

  const handleVideoReady = () => {
    setIsLoading(false);
  };

  return (
    <VideoCard>
      <VideoContainer>
        {isLoading && (
          <Skeleton variant="rectangular" height={300} width="100%" />
        )}
        <YouTube
          videoId={video.videoId}
          opts={{
            height: "300px",
            width: "100%"
          }}
          onReady={handleVideoReady}
          style={{ display: isLoading ? "none" : "block" }}
        />
      </VideoContainer>
      <h4>{video.title}</h4>
    </VideoCard>
  );
};

const Container = styled.div`
  padding-block: 48px;
`;

const SearchSection = styled.div`
  text-align: center;
  background: #f8f9fa;
  padding: 3rem 1rem;
  border-radius: 10px;
  margin-bottom: 2rem;

  h1 {
    color: ${({ theme }) => theme.palette.primary.main};
    margin: 0;
    margin-bottom: 1rem;
    font-size: 2rem;
  }

  p {
    margin: 0;
    margin-bottom: 2rem;
  }
`;

const SearchBar = styled.div`
  display: flex;
  max-width: 600px;
  margin: 0 auto;
  gap: 1rem;

  input {
    flex: 1;
    padding: 0.75rem 1rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    outline: none;

    &:focus {
      border-color: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;

const SearchButton = styled.button`
  background: ${({ theme }) => theme.palette.primary.main};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const VideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  padding: 0.5rem 0;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const VideoCard = styled.div`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  cursor: pointer;

  h4 {
    padding-block: 1rem;
    margin: 0;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
`;

const NoResults = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 3rem;
  background: #f8f9fa;
  border-radius: 10px;

  h3 {
    color: ${({ theme }) => theme.palette.primary.main};
    margin: 0;
    margin-bottom: 0.5rem;
  }

  p {
    margin: 0;
  }
`;
